import { useState } from 'react';
import { membershipService } from 'services/api';
import AvisoModal from 'components/AvisoModal';
import { RENEW_OPTIONS, daysLeft, StatusBadge, ExpiryCell, asambleasIncluidas, txtAsambleas } from './Memberships';
import styles from './MembershipRenewModal.module.css';

const MAX_DIAS = 3650; // 10 años — mismo tope que valida el backend (membershipsController.js)

export default function MembershipRenewModal({ property, onClose, onSaved }) {
  const [days,   setDays]   = useState(365);
  const [custom, setCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);

  const currentExpiry = property.subscription_expires_at;
  const isExpired     = currentExpiry && daysLeft(currentExpiry) < 0;
  const base          = currentExpiry && !isExpired ? new Date(currentExpiry) : new Date();
  const diasValidos   = Number.isInteger(parseInt(days)) && parseInt(days) >= 1 && parseInt(days) <= MAX_DIAS;
  const newExpiry     = new Date(base); if (diasValidos) newExpiry.setDate(newExpiry.getDate() + parseInt(days));
  // F-05: en Estándar/Premium la renovación REEMPLAZA el cupo del ciclo (no lo
  // suma, ver membershipsController.renovar). Renovar 1 mes un Premium con 3
  // asambleas disponibles lo dejaba en 0 sin avisar.
  const nuevasAsambleas = diasValidos ? asambleasIncluidas(property.subscription_type, days) : null;
  const quedanHoy       = Math.max(0, (property.assembly_allotment || 0) - (property.assemblies_created_this_cycle || 0));
  const bajaElCupo      = nuevasAsambleas !== null && (nuevasAsambleas === 0 || nuevasAsambleas < quedanHoy);

  const renew = async () => {
    if (!diasValidos) return setError(`Los días deben ser un número entero entre 1 y ${MAX_DIAS}`);
    setSaving(true); setError(null);
    try {
      await membershipService.renew(property.id, { days: parseInt(days), status: 'active' });
      onSaved(`Membresía renovada — nuevo vencimiento: ${newExpiry.toLocaleDateString('es-CO')}` +
        (nuevasAsambleas !== null ? ` — el nuevo ciclo incluye ${txtAsambleas(nuevasAsambleas)}` : ''));
    } catch (err) {
      setError(err.response?.data?.message || 'Error al renovar');
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className={`modal ${styles.caja}`} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <h3><i className={`fas fa-rotate-right ${styles.iconoTitulo}`} />Renovar membresía</h3>
            <div className={styles.subtitulo}>{property.name}</div>
          </div>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
        <div className={`modal-body ${styles.cuerpo}`}>
          {error && <AvisoModal>{error}</AvisoModal>}

          <div className={styles.estadoActual}>
            <div>
              <div className={styles.etiquetaMayus}>Estado actual</div>
              <StatusBadge status={property.subscription_status} expires_at={property.subscription_expires_at} />
            </div>
            <div className={styles.derecha}>
              <div className={styles.etiquetaMayus}>Vence</div>
              <ExpiryCell expires_at={property.subscription_expires_at} />
            </div>
          </div>

          <div>
            <label className="form-label">Período de renovación</label>
            <div className={styles.periodos}>
              {RENEW_OPTIONS.map(opt => (
                <button key={opt.days} type="button"
                  className={`btn btn-sm ${!custom && days === opt.days ? 'btn-primary' : 'btn-secondary'}`}
                  onClick={() => { setDays(opt.days); setCustom(false); }}>
                  {opt.label}
                </button>
              ))}
              <button type="button"
                className={`btn btn-sm ${custom ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setCustom(true)}>
                Personalizado
              </button>
            </div>
          </div>

          {custom && (
            <div className={`form-group ${styles.sinMargen}`}>
              <label className="form-label">Días a renovar</label>
              <input type="number" className="form-control" min={1} max={MAX_DIAS}
                value={days} onChange={e => setDays(e.target.value)} />
            </div>
          )}

          <div className={styles.nuevoVencimiento}>
            <i className={`fas fa-calendar-check ${styles.nuevoIcono}`} />
            <div>
              <div className={styles.nuevoEtiqueta}>Nuevo vencimiento</div>
              <div className={styles.nuevoFecha}>
                {diasValidos ? newExpiry.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' }) : '—'}
              </div>
              <div className={styles.nuevoNota}>
                {isExpired ? 'Se calcula desde hoy (membresía vencida)' : 'Se extiende desde el vencimiento actual'}
              </div>
            </div>
          </div>

          {nuevasAsambleas !== null && (
            <div className={bajaElCupo ? `alert alert-warning ${styles.avisoCupo}` : `${styles.avisoCupo} ${styles.avisoCupoSuave}`}>
              <i className={`fas ${bajaElCupo ? 'fa-triangle-exclamation' : 'fa-gavel'} ${styles.avisoIcono}`} />
              El nuevo ciclo incluye <strong>{txtAsambleas(nuevasAsambleas)}</strong> y reemplaza el cupo actual
              (hoy le quedan {txtAsambleas(quedanHoy)}). Las asambleas extra no cambian.
              {nuevasAsambleas === 0 && ' Con este período el conjunto no podrá crear asambleas salvo que le agregues asambleas extra.'}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancelar</button>
          <button className={`btn btn-primary ${styles.botonRenovar}`} onClick={renew} disabled={saving || !diasValidos}>
            {saving
              ? <><i className="fas fa-spinner fa-spin" /> Renovando...</>
              : <><i className="fas fa-rotate-right" /> Renovar por {days} días</>}
          </button>
        </div>
      </div>
    </div>
  );
}
